<template>
  <!--
    Empfänger einer Telefonnotiz (Mail Kanzlei Polz 30.09./01.10.2026):
    Kollegen der eigenen Organisation – Sachbearbeiter der Akte vorausgewählt –
    und externe Adressen, auch mehrere, wie in der Aktenübersicht. Gemeinsam
    für „Telefonat vermerken“ und die nachträgliche Mitteilung.
  -->
  <section class="nrf" aria-labelledby="nrf-title">
    <div class="nrf__head">
      <Mail :size="15" />
      <h3 id="nrf-title" class="nrf__title">{{ $gt('Mitteilung per E-Mail') }}</h3>
    </div>

    <v-autocomplete
      :model-value="modelValue.userIds"
      :items="recipients"
      item-title="name"
      item-value="idUser"
      :item-props="recipientItemProps"
      :label="$gt('Interne Empfänger')"
      :loading="loading"
      :no-data-text="$gt('Keine Mitarbeiter mit E-Mail-Adresse gefunden')"
      multiple
      chips
      closable-chips
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      @update:model-value="update({ userIds: $event })"
    />
    <p v-if="loadError" class="nrf__hint nrf__hint--error">{{ loadError }}</p>
    <p v-else-if="missingSachbearbeiter" class="nrf__hint">
      {{ $gt('Für diese Akte ist kein Sachbearbeiter eingetragen – bitte Empfänger selbst wählen.') }}
    </p>

    <v-combobox
      :model-value="modelValue.emails"
      :items="externalItems"
      item-title="label"
      item-value="email"
      :return-object="false"
      :label="$gt('Externe Empfänger (E-Mail-Adressen)')"
      :hint="$gt('Adresse eingeben und mit Enter bestätigen – mehrere möglich')"
      :error-messages="externalError"
      :delimiters="[',', ';', ' ']"
      multiple
      chips
      closable-chips
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      @update:model-value="update({ emails: $event })"
    >
      <template #chip="{ props, item }">
        <v-chip v-bind="props" :text="item.value" />
      </template>
    </v-combobox>

    <v-checkbox
      :model-value="modelValue.callbackFollowup"
      :disabled="!caseId || modelValue.userIds.length === 0"
      :label="$gt('Rückruf erforderlich (Wiedervorlage anlegen)')"
      :hint="followupHint"
      :persistent-hint="!!followupHint"
      color="primary"
      density="compact"
      hide-details="auto"
      class="nrf__followup"
      @update:model-value="update({ callbackFollowup: !!$event })"
    />
  </section>
</template>

<script>
import { Mail } from "lucide-vue-next";
import { listNotifyRecipients } from "@/services/phoneCallsApi";
import { useAuthStore } from "@/stores/authStore";
import { contactDisplayName, invalidExternalEmails, mergeRecipientSelection } from "@/utils/phoneCallNote";

export default {
  name: "NoteRecipientsFields",
  components: { Mail },
  props: {
    /** { userIds: number[], emails: string[], callbackFollowup: boolean } */
    modelValue: { type: Object, required: true },
    /** Fall der Notiz – bestimmt die vorausgewählten Sachbearbeiter. */
    caseId: { type: Number, default: null },
    /** Vorschläge für externe Empfänger: [{ name, email, role }] */
    externalSuggestions: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "update:loading"],
  data() {
    return {
      recipients: [],
      loading: false,
      loadError: "",
      // Verwirft Antworten älterer Anfragen (Fallwechsel während des Ladens).
      request: 0,
    };
  },
  computed: {
    missingSachbearbeiter() {
      return !!this.caseId && !this.loading && this.recipients.length > 0 &&
        !this.recipients.some((r) => r.isSachbearbeiter);
    },
    /** Vorschläge ohne Kollegen der eigenen Organisation – die stehen intern. */
    externalItems() {
      const internal = new Set(this.recipients.map((r) => (r.email || "").trim().toLowerCase()).filter(Boolean));
      const seen = new Set();
      const out = [];
      for (const s of this.externalSuggestions || []) {
        const email = (s.email || "").trim().toLowerCase();
        if (!email || internal.has(email) || seen.has(email)) continue;
        seen.add(email);
        const name = contactDisplayName(s.name);
        out.push({ email, label: [name !== email ? name : "", s.role ? `(${s.role})` : "", `<${email}>`].filter(Boolean).join(" ") });
      }
      return out;
    },
    externalError() {
      const invalid = invalidExternalEmails(this.modelValue.emails || []);
      return invalid.length ? `${this.$gt("Keine gültige E-Mail-Adresse")}: ${invalid.join(", ")}` : "";
    },
    followupHint() {
      if (!this.caseId) return this.$gt("Eine Wiedervorlage ist nur zu einem Fall möglich.");
      return "";
    },
  },
  watch: {
    caseId: { immediate: true, handler() { this.load(); } },
    loading(value) { this.$emit("update:loading", value); },
  },
  methods: {
    update(patch) {
      const next = { ...this.modelValue, ...patch };
      if (next.userIds.length === 0) next.callbackFollowup = false;
      this.$emit("update:modelValue", next);
    },
    recipientItemProps(recipient) {
      return { subtitle: recipient.isSachbearbeiter ? this.$gt("Sachbearbeiter der Akte") : undefined };
    },
    async load() {
      const request = ++this.request;
      this.loading = true;
      this.loadError = "";
      try {
        const list = await listNotifyRecipients(this.caseId);
        if (request !== this.request) return;
        this.recipients = Array.isArray(list) ? list : [];
        const selfId = Number(useAuthStore().idUser) || null;
        this.update({ userIds: mergeRecipientSelection(this.modelValue.userIds, this.recipients, selfId) });
      } catch {
        if (request !== this.request) return;
        this.recipients = [];
        this.update({ userIds: [] });
        this.loadError = this.$gt("Die Mitarbeiter konnten nicht geladen werden. Externe Empfänger sind weiter möglich.");
      } finally {
        if (request === this.request) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Abgesetzte Fläche, damit klar ist, dass beim Speichern zusätzlich eine
   Mail rausgeht. */
.nrf {
  margin-top: 8px;
  padding: 12px 14px 6px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #f5f9ff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.nrf__head { display: flex; align-items: center; gap: 7px; color: #1d4ed8; }
.nrf__title { margin: 0; font-size: 0.86rem; font-weight: 650; }
.nrf__hint { margin: -4px 0 0; font-size: 0.78rem; color: #b45309; }
.nrf__hint--error { color: #b91c1c; }
.nrf__followup { margin-left: -6px; }
</style>
